import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Hand, Sparkles } from "lucide-react";

const BSAVE_URL = "https://www.b-save.fr/";

const HIGHLIGHTS = [
  {
    icon: Hand,
    title: "Équipement Premium",
    body: "Gants de gardien haut de gamme, conçus par et pour des gardiens professionnels.",
  },
  {
    icon: ShieldCheck,
    title: "Performance & Durabilité",
    body: "Une accroche exceptionnelle et une résistance pensée pour les exigences du haut niveau.",
  },
  {
    icon: Sparkles,
    title: "Marque Française",
    body: "Une marque française reconnue dans le milieu du gardien, choisie par nos soins pour sa qualité.",
  },
];

export default function Partners() {
  return (
    <section
      id="partners"
      data-testid="partners-section"
      className="relative bg-[#050505] py-24 md:py-32 px-6 md:px-12 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9]"
            >
              <span className="text-[#CCFF00]">PARTENAIRE.</span>
            </motion.h2>
          </div>
          <p className="lg:col-span-5 text-zinc-400 text-base md:text-lg leading-relaxed">
            Nous collaborons avec des marques de référence pour offrir aux
            gardiens un équipement à la hauteur de leurs ambitions.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="bg-[#0a0a0a] border border-white/10 rounded-sm overflow-hidden"
          data-testid="partner-bsave-card"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12">
            {/* Left: brand + description + CTA */}
            <div className="lg:col-span-7 p-8 md:p-12 lg:p-16 flex flex-col">
              <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-6">
                Partenaire officiel
              </p>
              <div className="font-display text-7xl md:text-8xl leading-none tracking-tight">
                B<span className="text-[#CCFF00]">-</span>SAVE
              </div>

              <p className="mt-8 text-zinc-300 text-base md:text-lg leading-relaxed max-w-xl">
                Nous avons noué un partenariat avec{" "}
                <span className="text-white font-semibold">B-Save</span>,
                marque française spécialisée dans les gants de gardien de but
                haut de gamme. Une collaboration naturelle entre deux univers
                qui partagent les mêmes valeurs : exigence, performance et
                passion du poste de gardien.
              </p>

              <p className="mt-4 text-zinc-400 text-sm md:text-base leading-relaxed max-w-xl">
                Cette collaboration permet aux gardiens que nous accompagnons
                de découvrir et d&apos;accéder à un équipement professionnel, testé
                au plus haut niveau et adapté à toutes les exigences du poste.
              </p>

              <a
                href={BSAVE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="partner-bsave-cta"
                className="mt-10 inline-flex items-center justify-center gap-3 self-start bg-[#CCFF00] text-black font-bold uppercase tracking-wide text-sm px-8 py-4 rounded-sm hover:bg-white transition-colors group"
              >
                Découvrir notre partenaire
                <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>

            {/* Right: highlights */}
            <div className="lg:col-span-5 bg-[#070707] border-t lg:border-t-0 lg:border-l border-white/10 p-8 md:p-12 flex flex-col gap-6 justify-center">
              {HIGHLIGHTS.map((h, i) => {
                const Icon = h.icon;
                return (
                  <motion.div
                    key={h.title}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
                    className="flex items-start gap-4"
                  >
                    <span className="w-11 h-11 grid place-items-center border border-white/10 flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#CCFF00]" strokeWidth={1.5} />
                    </span>
                    <div>
                      <p className="font-display text-xl leading-none text-white">
                        {h.title}
                      </p>
                      <p className="mt-2 text-xs text-zinc-400 leading-relaxed">
                        {h.body}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
